import { lessonOver } from '../utils/callEnd';
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToLiveCallsForUser, hasLiveCallEnded } from '../services/liveCallBookings';
import type { LiveCallBooking } from '../types/liveCall';
import { recall, remember } from '../services/liveListStore';
import { useLessonClock } from './useLessonClock';
import { playerNoShowOpen } from '../utils/playerNoShow';
import { withPatch } from '../services/optimistic';
import { useOptimisticVersion } from './useOptimistic';

/**
 * The signed-in user's live calls for the Lessons screen, split the way its
 * tabs are: upcoming (soonest first) and ended (most recent first).
 * Re-partitions every minute so a call moves to Delivered once its room closes.
 */
/** How long a finished lesson stays on Upcoming as "ready in a few minutes" before it moves to Delivered. */

export function useLiveCalls(role: 'player' | 'coach'): {
  upcoming: LiveCallBooking[];
  ended: LiveCallBooking[];
  /** The first read has landed (or the list was kept from before). */
  loaded: boolean;
} {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;
  const key = `liveCalls:${role}:${uid ?? ''}`;
  // From the store first, so Lessons after a room shows its list at once, never a blank one.
  const [stored, setBookings] = useState<LiveCallBooking[]>(() => recall<LiveCallBooking[]>(key) ?? []);
  // The rows draw an optimistic action's finished lesson too (speed.md 2, services/optimistic).
  useOptimisticVersion();
  const bookings = stored.map((b) => withPatch(b));
  const [loaded, setLoaded] = useState(() => recall<LiveCallBooking[]>(key) !== undefined);
  // Re-partitioned when a lesson's window ends (useLessonClock), not every minute.
  const now = useLessonClock(bookings);

  useEffect(() => {
    if (!uid) return;
    return subscribeToLiveCallsForUser(role, uid, (list) => { setBookings(remember(key, list)); setLoaded(true); });
  }, [role, uid, key]);


  // Dev tools keeps time-based ended calls on the active tab (keeps old test bookings joinable).
  // Explicit terminal statuses always partition to ended regardless of dev mode.
  // A finished lesson is delivered from its finish, whatever its status still
  // says (finding 18): its row there says "Recording and lesson notes will be
  // ready in a few minutes". Dev
  // tools' reason (keeping test bookings joinable) does not apply to one that ended.
  const isEnded = (b: LiveCallBooking) =>
    // Decisions 95 and 140: a single the coach missed stays in In Progress, both roles, until it is rebooked or refunded.
    // 154: a player no-show the coach hasn't decided, or a single offered back to the player, too.
    !(b.missResolution?.status === 'pending' || playerNoShowOpen(b)) && (
    ['completed', 'no_show_player', 'no_show_coach'].includes(b.status) ||
    // Rated: Delivered at once, its row saying the call is open while it is (decision 105; the coach's, 106).
    !!b.ratedAt ||
    // Over: finished, both Done, or its call closed 15 minutes after the end (53, 56).
    (b.status === 'confirmed' && lessonOver(b, now)) ||
    (!import.meta.env.VITE_ENABLE_DEV_TOOLS && hasLiveCallEnded(b, now)));

  const upcoming = bookings
    .filter((b) => !isEnded(b))
    .sort((a, b) => a.scheduledStartAt.localeCompare(b.scheduledStartAt));
  const ended = bookings
    .filter((b) => isEnded(b))
    .sort((a, b) => b.scheduledStartAt.localeCompare(a.scheduledStartAt));

  return { upcoming, ended, loaded };
}
